import { useState, useEffect } from 'react';
import { motion, useMotionValue, useTransform, PanInfo } from 'framer-motion';
import { Heart, X, MapPin, DollarSign, Briefcase, Sparkles } from 'lucide-react';
import { SafeJob, safeJobs } from '../lib/safeJobData';
import { AIChatBubble, ChatMessage, getRandomMessage, ChatMessageType } from './AIChatBubble';

export function SafeSwipeDemo() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [exitDirection, setExitDirection] = useState<'left' | 'right' | null>(null);
  const [chatMessage, setChatMessage] = useState<ChatMessage | null>(null);
  const [messageCounter, setMessageCounter] = useState(0);
  const [isFirstCard, setIsFirstCard] = useState(true);

  useEffect(() => {
    if (isFirstCard) {
      showChatMessage('greeting');
      setIsFirstCard(false);
    }
  }, [isFirstCard]);

  const showChatMessage = (type: ChatMessageType) => {
    const text = getRandomMessage(type);
    const newMessage: ChatMessage = {
      id: `${Date.now()}-${messageCounter}`,
      text,
      type,
    };
    setChatMessage(newMessage);
    setMessageCounter((prev) => prev + 1);

    setTimeout(() => {
      setChatMessage(null);
    }, 6000);
  };

  const handleSwipe = (direction: 'left' | 'right') => {
    setExitDirection(direction);

    if (direction === 'right') {
      showChatMessage('swipe-right');
    } else {
      showChatMessage('swipe-left');
    }

    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % safeJobs.length);
      setExitDirection(null);

      setTimeout(() => {
        if (Math.random() > 0.6) {
          showChatMessage('help-offer');
        } else {
          showChatMessage('greeting');
        }
      }, 400);
    }, 300);
  };

  const visibleJobs = [
    safeJobs[currentIndex],
    safeJobs[(currentIndex + 1) % safeJobs.length],
    safeJobs[(currentIndex + 2) % safeJobs.length],
  ];

  return (
    <div className="relative w-full max-w-6xl mx-auto">
      <div className="flex flex-col lg:flex-row items-start justify-center gap-8">
        <div className="relative w-full max-w-md mx-auto flex flex-col items-center">
          <div className="relative h-[580px] flex items-center justify-center w-full mb-6">
            {visibleJobs.map((job, index) => (
              <SwipeCard
                key={`${job.id}-${currentIndex}-${index}`}
                job={job}
                index={index}
                onSwipe={index === 0 ? handleSwipe : undefined}
                exitDirection={index === 0 ? exitDirection : null}
                isTopCard={index === 0}
              />
            ))}
          </div>

          <div className="flex items-center gap-6 z-20">
            <button
              onClick={() => handleSwipe('left')}
              className="w-16 h-16 bg-white rounded-full shadow-2xl flex items-center justify-center hover:bg-red-50 transition-all group border-2 border-gray-100 hover:scale-110 active:scale-95"
              aria-label="Skip this job"
            >
              <X className="w-8 h-8 text-red-500 group-hover:scale-110 transition-transform" />
            </button>

            <button
              onClick={() => handleSwipe('right')}
              className="w-16 h-16 bg-white rounded-full shadow-2xl flex items-center justify-center hover:bg-green-50 transition-all group border-2 border-gray-100 hover:scale-110 active:scale-95"
              aria-label="Apply to this job"
            >
              <Heart className="w-8 h-8 text-green-500 group-hover:scale-110 transition-transform" />
            </button>
          </div>
        </div>

        <div className="w-full max-w-md lg:max-w-sm min-h-[100px] flex items-center justify-center px-4 pt-8 lg:pt-12">
          <AIChatBubble message={chatMessage} />
        </div>
      </div>
    </div>
  );
}

interface SwipeCardProps {
  job: SafeJob;
  index: number;
  onSwipe?: (direction: 'left' | 'right') => void;
  exitDirection: 'left' | 'right' | null;
  isTopCard: boolean;
}

function SwipeCard({ job, index, onSwipe, exitDirection, isTopCard }: SwipeCardProps) {
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-250, 250], [-20, 20]);
  const opacity = useTransform(x, [-250, -150, 0, 150, 250], [0.5, 1, 1, 1, 0.5]);

  const handleDragEnd = (_: any, info: PanInfo) => {
    if (!onSwipe) return;

    const threshold = 100;
    if (Math.abs(info.offset.x) > threshold) {
      onSwipe(info.offset.x > 0 ? 'right' : 'left');
    }
  };

  return (
    <motion.div
      className="absolute w-full max-w-sm bg-white rounded-xl shadow-lg overflow-hidden cursor-grab active:cursor-grabbing select-none border border-gray-100"
      style={{
        x: isTopCard ? x : 0,
        rotate: isTopCard ? rotate : 0,
        opacity: isTopCard ? opacity : 1,
        zIndex: 20 - index,
        scale: 1 - index * 0.04,
        y: index * 8,
      }}
      drag={isTopCard && !exitDirection ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.7}
      onDragEnd={handleDragEnd}
      animate={
        exitDirection
          ? {
              x: exitDirection === 'right' ? 500 : -500,
              opacity: 0,
              rotate: exitDirection === 'right' ? 25 : -25,
              transition: { duration: 0.3, ease: 'easeIn' },
            }
          : {}
      }
      initial={false}
    >
      {isTopCard && (
        <>
          <motion.div
            className="absolute inset-0 bg-green-500/20 backdrop-blur-sm flex items-center justify-center pointer-events-none z-10"
            style={{ opacity: useTransform(x, [0, 100], [0, 1]) }}
          >
            <div className="bg-green-500 text-white px-8 py-3 rounded-full font-bold text-xl shadow-lg transform rotate-12">
              APPLY
            </div>
          </motion.div>
          <motion.div
            className="absolute inset-0 bg-red-500/20 backdrop-blur-sm flex items-center justify-center pointer-events-none z-10"
            style={{ opacity: useTransform(x, [-100, 0], [1, 0]) }}
          >
            <div className="bg-red-500 text-white px-8 py-3 rounded-full font-bold text-xl shadow-lg transform -rotate-12">
              SKIP
            </div>
          </motion.div>
        </>
      )}

      <div className="bg-white border-b-2 border-gray-100 h-20 relative flex items-center px-6">
        <div className="w-12 h-12 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-xl flex items-center justify-center shadow-md">
          <Briefcase className="w-6 h-6 text-white" />
        </div>
      </div>

      <div className="p-6 space-y-4">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-1 leading-tight">
            {job.title}
          </h3>
          <p className="text-lg text-gray-600 font-medium">{job.company}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {job.tags.map((tag) => (
            <span
              key={tag}
              className="px-3 py-1.5 bg-gradient-to-r from-cyan-50 to-blue-50 text-cyan-700 rounded-full text-xs font-semibold border border-cyan-200"
            >
              {tag}
            </span>
          ))}
        </div>

        <div className="space-y-2.5">
          <div className="flex items-center gap-2.5 text-gray-700">
            <DollarSign className="w-5 h-5 text-green-600" />
            <span className="text-base font-semibold">{job.salary}</span>
          </div>
          <div className="flex items-center gap-2.5 text-gray-600">
            <MapPin className="w-5 h-5 text-blue-600" />
            <span className="text-sm">{job.location}</span>
          </div>
          <div className="flex items-center gap-2.5 text-gray-600">
            <Briefcase className="w-5 h-5 text-purple-600" />
            <span className="text-sm">{job.type}</span>
          </div>
        </div>

        <p className="text-gray-600 leading-relaxed text-sm">{job.description}</p>

        <div className="pt-3 border-t border-gray-100">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="w-4 h-4 text-cyan-500" />
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
              Matching Skills
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {job.matchingSkills.map((skill) => (
              <span
                key={skill}
                className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-lg text-xs font-medium border border-cyan-200"
              >
                {skill}
              </span>
            ))}
          </div>
        </div>

        {isTopCard && (
          <div className="pt-3 border-t border-gray-100">
            <p className="text-xs text-gray-400 text-center font-medium">
              ← Swipe or tap buttons below →
            </p>
          </div>
        )}
      </div>
    </motion.div>
  );
}
